import type { ComponentType, JSX, ReactElement, ReactNode, Ref } from 'react';
import { forwardRef, isValidElement } from 'react';

import type { SvgIconProps } from './SvgIcon';

/**
 * Props `SizedIcon` hands the glyph. Material icons (`./material/<Name>`) take them as `SvgIconProps`;
 * several `./CustomIcons/` glyphs read `size` instead, so `SizedIcon` always passes it as a number.
 */
type IconGlyphProps = Pick<SvgIconProps, 'htmlColor' | 'className' | 'style' | 'titleAccess'> & {
    size?: number;
    ref?: Ref<SVGSVGElement>;
};

/** A glyph component, imported directly from `DesignSystem2/Icon/material/<Name>` or `DesignSystem2/Icon/CustomIcons/<Name>`. */
export type IconComponent = ComponentType<IconGlyphProps>;

interface IconProps {
    color?: string;
    size?: string | number;
    className?: string;
    titleAccess?: string;
}

interface SizedIconProps extends IconProps {
    icon: IconComponent;
}

/**
 * Renders a glyph the way DS2 icons always have: `color` becomes the svg's `color` attribute,
 * `size` (default 24px) becomes `font-size` with `vertical-align: middle`.
 *
 * Import glyphs individually and pass them in, so a page only ships the icons it renders:
 *
 *     import AddIcon from 'DesignSystem2/Icon/material/Add';
 *     <SizedIcon icon={AddIcon} size={16} />
 */
const SizedIcon = forwardRef(function SizedIcon(
    { icon: Glyph, color = 'inherit', size = '24px', className, ...props }: SizedIconProps,
    ref: Ref<SVGSVGElement>,
): JSX.Element {
    const numericSize = typeof size === 'number' ? size : parseInt(size, 10) || 24;
    return (
        <Glyph
            htmlColor={color}
            className={className}
            style={{ fontSize: size, verticalAlign: 'middle' }}
            size={numericSize}
            {...props}
            ref={ref}
        />
    );
});

export default SizedIcon;

/** A glyph bound into a component taking `SizedIcon`'s props; see `toSizedIcon`. */
type SizedIconComponent = (props: IconProps & { ref?: Ref<SVGSVGElement> }) => ReactNode;

const sizedIconCache = new WeakMap<IconComponent, SizedIconComponent>();

/**
 * `glyph` as a component taking `SizedIcon`'s props, for data that carries icon components
 * (`{ label: 'Done', Icon: toSizedIcon(CheckIcon) }`). To render a glyph, use `SizedIcon` directly.
 * Cached per glyph, so it is safe to call during render without remounting.
 */
export function toSizedIcon(glyph: IconComponent): SizedIconComponent {
    let sized = sizedIconCache.get(glyph);
    if (!sized) {
        sized = forwardRef<SVGSVGElement, IconProps>(function Sized(props, ref) {
            return <SizedIcon icon={glyph} {...props} ref={ref} />;
        }) as unknown as SizedIconComponent;
        sizedIconCache.set(glyph, sized);
    }
    return sized;
}

/** Tells a glyph component apart from a rendered element, for props that accept either. */
export function isIconComponent(value: unknown): value is IconComponent {
    if (typeof value === 'function') return true;
    // `memo` / `forwardRef` components are objects tagged with `$$typeof`; so are elements.
    return typeof value === 'object' && value !== null && '$$typeof' in value && !isValidElement(value);
}

/** A glyph component or a ready-made element. */
export type IconOrElement = IconComponent | ReactElement;
